<template>
  <v-menu
    class="date-time-input__menu"
    ref="menu"
    v-model="menu"
    :close-on-content-click="false"
    transition="scale-transition"
    offset-y
    max-width="290px"
    min-width="290px"
  >
    <template v-slot:activator="{ on }">
      <v-form class="date-time-input">
        <!-- Blocks parent forms from registering internal v-text-field -->
        <v-text-field
          ref="textField"
          v-on="on"
          :label="label"
          :value="textFieldValue"
          :disabled="disabled"
          @input="textFieldInput"
          @change="textFieldChange"
          :hide-details="hideDetails"
          :append-icon="appendIcon"
          :append-outer-icon="appendOuterIcon"
          :clearable="clearable"
          :background-color="backgroundColor"
          :dense="dense"
          :error="error"
          :error-messages="errorMessages"
          :hint="hint"
          :nudge-right="prependIcon ? '33px' : null"
          :persistent-hint="persistentHint"
          :placeholder="placeholder"
          :prepend-icon="prependIcon"
          :prepend-inner-icon="prependInnerIcon"
          :readonly="readonly"
          :rules="dateTimeRules"
          :single-line="singleLine"
          @blur="$emit('blur', $event)"
          @click="$emit('click', $event)"
          @click:clear="clear"
          autocomplete="false"
        ></v-text-field>
      </v-form>
    </template>
    <DateTimePicker
      v-if="menu && !readonly"
      ref="dateTimePicker"
      :value="pickerDateTime"
      :datePickerProps="datePickerProps"
      @input="dateTimePickerInput"
      @change="dateTimePickerChange"
    ></DateTimePicker>
  </v-menu>
</template>

<script>
import DateTimePicker from './DateTimePicker'
import { DateTime } from 'luxon'

const DEFAULT_DATETIME_FORMAT = 'yyyy-MM-dd h:mm a'

export default {
  name: 'DateTimeInput',
  props: {
    value: {
      type: String,
      description: 'ISO date time string in UTC'
    },
    zone: {
      type: String,
      description: 'time zone to convert to for display. Default is undefined which should make it "local" browser time'
    },
    displayFormat: {
      type: String,
      default: DEFAULT_DATETIME_FORMAT
    },
    appendIcon: String,
    appendOuterIcon: String,
    backgroundColor: String,
    clearable: Boolean,
    dense: Boolean,
    disabled: Boolean,
    error: Boolean,
    errorMessages: [String, Array],
    hideDetails: [Boolean, String],
    hint: String,
    label: String,
    persistentHint: Boolean,
    placeholder: String,
    prependIcon: String,
    prependInnerIcon: String,
    readonly: Boolean,
    rules: Array,
    singleLine: Boolean,
    minDate: String,
    maxDate: String
  },
  inject: {
    form: { default: null }
  },
  data() {
    return {
      textField: null,
      menu: false,
      originalDateTime: null,
      dateTime: null,
      valueIsString: false
    }
  },
  components: {
    DateTimePicker
  },
  computed: {
    textFieldValue() {
      return this.dateTime ? this.dateTime.toFormat(this.displayFormat) : ''
    },
    pickerDateTime() {
      return this.dateTime
        ? this.dateTime.toISO({ suppressSeconds: true, suppressMilliseconds: true, includeOffset: false })
        : ''
    },
    dirty() {
      if (!this.originalDateTime) {
        return !!this.dateTime
      }
      return !this.dateTime.equals(this.originalDateTime)
    },
    datePickerProps() {
      return {
        min: this.minDate,
        max: this.maxDate
      }
    },
    dateTimeRules() {
      if (this.menu) return []
      return this.textFieldRules()
    },
    hasError() {
      return this.textField?.hasError
    },
    errorBucket() {
      return this.textField ? this.textField.errorBucket : []
    }
  },
  watch: {
    value: {
      handler: function () {
        let dt = DateTime.fromISO(this.value, { zone: this.zone })
        this.originalDateTime = this.dateTime = dt.isValid ? dt : null
      },
      immediate: true
    },
    menu() {
      if (!this.menu && this.dirty) {
        this.$emit('change', this.dateTime.toUTC().toISO({ suppressMilliseconds: true }))
      }

      if (this.menu) {
        window.addEventListener('focusin', this.onOtherFocused)
      } else {
        window.removeEventListener('focusin', this.onOtherFocused)
      }
    }
  },
  methods: {
    clear(event) {
      if (!this.readonly) this.$emit('click:clear', event)
    },
    dateTimePickerInput(newDateTime) {
      this.dateTime = DateTime.fromISO(newDateTime, { zone: this.zone })
      this.$emit('input', this.dateTime.toUTC().toISO({ suppressMilliseconds: true }))
    },
    dateTimePickerChange(newDateTime) {
      this.dateTime = DateTime.fromISO(newDateTime, { zone: this.zone })
      this.menu = false
    },
    textFieldInput(newDateTimeText) {
      if (!newDateTimeText) {
        //  This is to handle the case of the user clearing the date either manually or with the X
        this.dateTime = null
        this.$emit('input', null)
        return
      }
      let dt = DateTime.fromFormat(newDateTimeText, this.displayFormat, { zone: this.zone })
      if (dt.isValid) {
        this.dateTime = dt
        this.$emit('input', this.dateTime.toUTC().toISO({ suppressMilliseconds: true }))
      }
    },
    textFieldChange() {
      if (this.dateTime === null || this.dateTime === undefined) {
        this.$emit('change', null)
      } else {
        this.$emit('change', this.dateTime.toUTC().toISO({ suppressMilliseconds: true }))
      }
    },
    isDateTime(value) {
      if (!value) {
        return true
      }
      let dt = DateTime.fromFormat(value, this.displayFormat, { zone: this.zone })
      return dt.isValid || 'Please enter a date & time.'
    },
    focus() {
      this.$refs.textField.focus()
    },
    blur() {
      this.$refs.textField.blur()
    },
    validate() {
      const valid = this.validateTextField()
      if (!valid) {
        this.$refs.textField.hasFocused = true
      }
      return valid
    },
    validateTextField() {
      const value = this.$refs.textField.internalValue
      return this.textFieldRules().every(rule => {
        if (typeof rule === 'function') return rule(value) === true
        return rule === true
      })
    },
    textFieldRules() {
      return this.rules ? [...this.rules, this.isDateTime] : [this.isDateTime]
    },
    reset() {
      return this.$refs.textField.reset()
    },
    resetValidation() {
      return this.$refs.textField.resetValidation()
    },
    onOtherFocused(event) {
      if (this.$refs.textField && this.$refs.textField.$el.contains(event.target)) return
      if (this.$refs.dateTimePicker && this.$refs.dateTimePicker.$el.contains(event.target)) return
      this.menu = false
    }
  },
  created() {
    this.form?.register(this)
  },
  mounted() {
    this.textField = this.$refs.textField
  },
  beforeDestroy() {
    this.form?.unregister(this)
  }
}
</script>

<style lang="scss" scoped>
.date-time-input {
  display: flex;
  align-items: flex-start;
  flex: 1 1 auto;
}
</style>
